/**
 * loginRedirect — preserves the user's intended destination across the
 * AuthGate → LoginPage → Home/dashboard bounce.
 *
 * Without this, a user pasting `app.sgpdev.com/#/Audit/auditdashboard?workorder=12345`
 * lands at `/Home/dashboard` after authenticating instead of the URL they came for.
 * That makes deep links (bookmarks, emails, redirects from the AngularJS host) feel
 * broken and undermines the Phase E coexistence story.
 *
 * Mechanism:
 *  1. `redirectToLogin()` calls `captureIntendedDestination()` BEFORE navigating
 *     to `/#/login`. We record the hash that was about to be lost.
 *  2. `LoginPage` calls `consumeIntendedDestination()` after a successful sign-in
 *     and navigates to that path (or `/Home/dashboard` as fallback).
 *
 * Storage: `sessionStorage` with a TTL. SessionStorage is cleared on tab close
 * which is the right scope — bookmarks across tabs/sessions resolve from the URL
 * itself, not from this cache.
 */

const STORAGE_KEY = 'spi_intended_destination';
const TTL_MS = 10 * 60 * 1000; // 10 minutes

interface StoredDestination {
  /** Hash including the leading `#` (e.g. `#/Audit/auditdashboard?workorder=12345`). */
  hash: string;
  /** Wall-clock time the value was written, used for TTL expiry. */
  capturedAt: number;
}

/** Routes we never want to bounce back to after login. */
const BLOCKED_PREFIXES = ['#/login', '#/Login', '#/logout', '#/Logout', '#/VendorConnect/Logout'];

function isBlocked(hash: string): boolean {
  return BLOCKED_PREFIXES.some((p) => hash.startsWith(p));
}

/**
 * Capture the current `window.location.hash` so the user can be returned there
 * after sign-in. No-op for hashes that point at the login/logout flow itself.
 */
export function captureIntendedDestination(hash: string = window.location.hash): void {
  if (!hash) return;
  if (isBlocked(hash)) return;
  // Skip the empty `#` hash too — that's just the root and we'd land there anyway.
  if (hash === '#' || hash === '#/') return;

  const payload: StoredDestination = { hash, capturedAt: Date.now() };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage full / disabled — silently skip; the user just lands at default.
  }
}

/**
 * Read and clear the captured destination. Returns the path (without leading `#`)
 * that React Router's `useNavigate` accepts, or `null` if no usable value exists.
 */
export function consumeIntendedDestination(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);

    const parsed = JSON.parse(raw) as StoredDestination;
    if (!parsed || typeof parsed.hash !== 'string') return null;
    if (Date.now() - parsed.capturedAt > TTL_MS) return null;
    if (isBlocked(parsed.hash)) return null;

    // Strip the leading `#` for `useNavigate(...)`. Preserve query string.
    return parsed.hash.startsWith('#') ? parsed.hash.slice(1) : parsed.hash;
  } catch {
    return null;
  }
}

/**
 * Drop any persisted destination without consuming it.
 *
 * Used when we deliberately do NOT want the user returned to the captured URL
 * — e.g. the rapid-bounce loop guard sends the user to the dashboard instead,
 * and a stale destination would otherwise bounce them back into the loop on
 * the next genuine re-auth.
 */
export function clearIntendedDestination(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Test-only: drop any persisted destination. */
export function __clearIntendedDestinationForTests(): void {
  clearIntendedDestination();
}
